import { useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PHASES } from "@/lib/lunelle-data";
import { infoForDate } from "@/lib/cycle";
import { faithDaysOn } from "@/lib/faith-customs";

const COLOR = Object.fromEntries(PHASES.map((p) => [p.id, p.color]));

const dayStyle = (cycle, info, iso, dueIso) => {
  if (cycle.pregnant) return iso === dueIso ? { background: "#9E4429", color: "#FBF5EE" } : { background: "#F4E6DC", color: "#2C221E" };
  if (!info) return { background: "#F4E6DC", color: "#2C221E" };
  const c = COLOR[info.phase];
  return info.phase === "menstrual" ? { background: c, color: "#FBF5EE" } : { background: `${c}2E`, color: "#2C221E" };
};

export const MonthGrid = ({ cycle, selected, onSelect, dueIso, faith }) => {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const days = eachDayOfInterval({ start: startOfWeek(month), end: endOfWeek(endOfMonth(month)) });
  return (
    <div data-testid="cycle-calendar">
      <div className="flex items-center justify-between">
        <button onClick={() => setMonth((m) => addMonths(m, -1))} data-testid="cycle-calendar-prev" aria-label="Previous month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"><ChevronLeft className="h-4 w-4" /></button>
        <p className="font-serif text-2xl text-[#2C221E]" data-testid="cycle-calendar-month">{format(month, "MMMM yyyy")}</p>
        <button onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="cycle-calendar-next" aria-label="Next month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider text-[#7A665C]">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i}>{d}</span>)}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1">
        {days.map((d) => {
          const iso = format(d, "yyyy-MM-dd");
          const info = infoForDate(cycle, d);
          const fertile = !cycle.pregnant && info?.fertile;
          const logged = Boolean(cycle.logs[iso]);
          const holy = faith ? faithDaysOn(faith, iso)[0] : null;
          return (
            <button key={iso} onClick={() => onSelect(iso)} data-testid={`cycle-day-${iso}`} title={info && !cycle.pregnant ? `Day ${info.day} · ${info.phase}${fertile ? " · fertile" : ""}` : format(d, "d MMM")}
              className={`relative flex aspect-square items-center justify-center rounded-xl text-sm transition-transform duration-200 hover:scale-105 ${isSameMonth(d, month) ? "" : "opacity-35"} ${isToday(d) ? "ring-2 ring-[#2C221E] ring-offset-1" : ""} ${selected === iso ? "outline outline-2 outline-offset-2 outline-[#9E4429]" : ""}`}
              style={dayStyle(cycle, info, iso, dueIso)}>
              {format(d, "d")}
              {fertile && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#3F5E57]" data-testid={`cycle-fertile-${iso}`} />}
              {holy && <span title={holy.name} className={`absolute left-1 top-1 h-1.5 w-1.5 rotate-45 ${holy.kind === "fast" ? "bg-[#D4A23C]" : "bg-[#6B4C8A]"}`} data-testid={`cycle-faithday-${iso}`} />}
              <span className="absolute bottom-1 flex gap-0.5">
                {cycle.starts.includes(iso) && <span className="h-1 w-1 rounded-full bg-[#FBF5EE]" />}
                {logged && <span className="h-1 w-1 rounded-full bg-[#2C221E]" data-testid={`cycle-logdot-${iso}`} />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
